import React, { useMemo, useState, useCallback } from 'react';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridColumnGroupingModel,
} from '@mui/x-data-grid';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MuiButton, MuiSwitch } from '@ace/ace-ui-web';
import pagesStandardStyles from '../../../../Pages.module.css';
import styles from '../Ecommerce.module.css';
import salesByFulfillmentStyles from './Salesbyfulfillment.module.css';
import { getDataGridStyles } from '../../../../GridStyles';
import { getGroupColumnHeaderStyles } from '../../../../GroupColumnHeaderGridStyles';
import {
  formatNumberWithCommas,
  roundToOneDecimal,
  roundToWholeNumber,
  changeColumnColorStatus,
  formatLoadDate,
  renderEmptyValue,
  isMobileDevice,
  formatNegativeChangeValue,
  renderPeerGroupRankCell,
  isSmallNonZeroValue,
  isValidApiDate,
  formatSmallChangeValue,
  minWidthForNumber,
} from '../../../../../utils/appUtils';
import { renderPercentCell } from '../../../../../utils/dataGridRender';
import {
  SalesPerformanceDetailsByFulfillment,
  ChangeYearValue,
} from '../../../../../models/SalesPerformanceDetailsByFulfillmentModel';
import lang from '../../../../../languages/en.json';
import { useAppSelector } from '../../../../../redux/hooks';
import {
  selectSalesByFulfillment,
  selectEcommLoading,
} from '../../../../../redux/PerformanceReducer/Ecommerce/ecommSalesPerfSlice';
// Access global state to determine if multi-store features are enabled
import { selectIsMultiStoreEnabled } from '../../../../../redux/UserReducer/userSlice';
import SkeletonEcommSalesPerfGrid from '../../../../../components/skeletons/performance/ecommerce/SkeletonEcommSalesPerfGrid';
import { useClickHandler } from '../../../../../utils/usageMetricsLogger';
import { useBuildDynamicUrl } from '../../../../../hooks/useDynamicUrl';

const BASE_URL_TYPE = 'WFC';
const PAGE_NM = 'Performance';
const TAB_NM = 'Ecommerce';
const SECTION_NM = 'Sales By Fulfillment';
const FIXED_COLUMN_WIDTH = 135;

// ─── Props interface ─────────────────────────────────────────────────────────

/** When set, only the specified column group is shown (used by print view to stack groups vertically) */
export type FulfillmentVisibleGroup = 'sales' | 'orders' | 'units' | 'grossProfit';

interface SalesByFulfillmentProps {
  /** Pre-fetched data for print view — skips Redux read and API fetch when provided */
  data?: SalesPerformanceDetailsByFulfillment | null;
  /** When true, hides interactive elements (peer group toggle, customize report, adjusts column widths) */
  isPrintView?: boolean;
  /** Show only a single column group — used to render 4 stacked grids in print view */
  visibleGroup?: FulfillmentVisibleGroup;
}

// Row interface for DataGrid - represents flattened structure
// To add new categories: Add category name to the union type below (e.g., | 'returned')
interface GridRow {
  id: number;
  fixedColumn: string;
  dataKey: string;
  category: 'demand' | 'fulfilled' | 'canceled'; // Determines which fulfillment category to access
  subcategory: 'totalForCategory' | 'bopis' | 'bodfs' | 'shipToHome'; // Determines which fulfillment type
}

// Helper function to format table cells with commas, currency and percentage
const renderFormatCell = (
  params: GridRenderCellParams<GridRow, string | number>,
): JSX.Element | string => {
  const emptyCheck = renderEmptyValue(params.value);
  if (emptyCheck) return emptyCheck;

  const columnHeader = params.colDef.headerName?.toLowerCase() || '';
  const columnField = params.field.toLowerCase();

  // Check if column belongs to Sales or Gross Profit group headers (not Orders or Units)
  const isSalesOrGrossProfitColumn =
    columnField.startsWith('sales') || columnField.startsWith('grossprofit');

  // Round currency values to whole numbers before formatting
  let valueToFormat = params.value;
  if (
    isSalesOrGrossProfitColumn &&
    (columnHeader === 'this year' || columnHeader === 'last year') &&
    typeof valueToFormat === 'number'
  ) {
    valueToFormat = roundToWholeNumber(valueToFormat);
  }

  // Format number with commas
  const formattedValue = formatNumberWithCommas(valueToFormat);

  // Add $ for currency columns (This Year, Last Year) in Sales/Gross Profit groups
  if (
    isSalesOrGrossProfitColumn &&
    (columnHeader === 'this year' || columnHeader === 'last year')
  ) {
    return `$${formattedValue}`;
  }

  // Default: just return formatted value with commas (Orders, Units, Peer Group Rank)
  return formattedValue;
};

// Helper function to render change cells with color based on status
const renderChangeCell = (
  params: GridRenderCellParams<GridRow, ChangeYearValue>,
): JSX.Element | string => {
  if (!params.value) {
    return renderEmptyValue(null);
  }

  const emptyCheck = renderEmptyValue(params.value.value);
  if (emptyCheck) return emptyCheck;

  const { value, status } = params.value;
  const columnField = params.field.toLowerCase();

  // Check if column belongs to Sales or Gross Profit groups (not Orders or Units)
  const isCurrency = columnField.startsWith('sales') || columnField.startsWith('grossprofit');

  // Convert value (string) to number for small value detection. If value is already a number, keep as is.
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  // Determine format and decimal places based on column type
  const format = isCurrency ? 'DOL' : 'VAL';
  // Round to 0 decimal places (whole numbers) for all "Change Over Last Year" columns across all groups
  // (Sales, Orders, Units, Gross Profit). This ensures small values like 0.4 display as <1 or <$1, not <1.0 or <$1.0
  const decimalPlaces = 0;

  // Check if this is a small non-zero value that would round to 0
  if (!isNaN(numericValue) && isSmallNonZeroValue(numericValue, decimalPlaces)) {
    const displayValue = formatSmallChangeValue(numericValue, format, decimalPlaces);
    // Value is confirmed non-zero by isSmallNonZeroValue, so derive color from its sign
    const smallValueStatus = numericValue > 0 ? 'positive' : 'negative';
    return <span style={{ color: changeColumnColorStatus(smallValueStatus) }}>{displayValue}</span>;
  }

  // Round all values to whole numbers (currency and non-currency)
  let valueToDisplay = value;
  if (typeof valueToDisplay === 'string') {
    valueToDisplay = roundToWholeNumber(valueToDisplay).toString();
  }

  // Use helper function to format change value with proper negative sign placement
  const displayValue = formatNegativeChangeValue(valueToDisplay, isCurrency);

  return <span style={{ color: changeColumnColorStatus(status) }}>{displayValue}</span>;
};

// Helper function to render change percent cells with color and small value detection (bounded notation)
const renderChangeNonZeroCell = (
  params: GridRenderCellParams<GridRow, ChangeYearValue>,
): JSX.Element | string => {
  if (!params.value) {
    return renderEmptyValue(null);
  }

  const emptyCheck = renderEmptyValue(params.value.value);
  if (emptyCheck) return emptyCheck;

  const { value, status } = params.value;

  // Convert value to number for small value detection
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  // Check if this is a small non-zero value that would round to 0 (percentages use 1 decimal place)
  if (!Number.isNaN(numericValue) && isSmallNonZeroValue(numericValue, 1)) {
    const displayValue = formatSmallChangeValue(numericValue, 'PCT', 1);
    // Value is confirmed non-zero by isSmallNonZeroValue, so derive color from its sign
    const smallValueStatus = numericValue > 0 ? 'positive' : 'negative';
    return <span style={{ color: changeColumnColorStatus(smallValueStatus) }}>{displayValue}</span>;
  }

  // Round to one decimal place and add % symbol
  const displayValue = `${formatNumberWithCommas(numericValue.toFixed(1))}%`;

  return <span style={{ color: changeColumnColorStatus(status) }}>{displayValue}</span>;
};

// Custom render function for benchmark percentage columns (displays % or dash for null)
const renderBenchmarkPercentCell = (
  params: GridRenderCellParams<GridRow, string | number | null>,
): JSX.Element | string | number => {
  const emptyCheck = renderEmptyValue(params.value);
  if (emptyCheck) return emptyCheck;

  const columnField = params.field.toLowerCase();

  // Check if this is the first benchmark column (not benchmark2)
  const isFirstBenchmark = !columnField.includes('benchmark2');

  // Check if column belongs to Sales or Gross Profit groups
  const isSalesOrGrossProfitColumn =
    columnField.startsWith('sales') || columnField.startsWith('grossprofit');

  // For Sales and Gross Profit groups: first benchmark = $, second benchmark2 = %
  if (isSalesOrGrossProfitColumn) {
    if (isFirstBenchmark) {
      // First benchmark for Sales/Gross Profit: format as currency (rounded to whole number)
      const roundedValue = roundToWholeNumber(params.value);
      const formattedValue = formatNumberWithCommas(roundedValue);
      return `$${formattedValue}`;
    } else {
      // Second benchmark2 for Sales/Gross Profit: format as percentage
      const roundedValue = roundToOneDecimal(params.value);
      return `${roundedValue}%`;
    }
  }

  // For Orders and Units groups: first benchmark = plain number, benchmark2 = %
  if (isFirstBenchmark) {
    return Math.round(Number(params.value)); // Return whole number
  }

  // For all other cases (benchmark2 for Orders/Units), format as percentage
  const roundedValue = roundToOneDecimal(params.value);
  return `${roundedValue}%`;
};

// Shared benchmark column header — renders an info icon + tooltip alongside the
// "80% / Benchmark" two-line layout. Reused by both benchmark columns to avoid
// duplicated JSX.
const BenchmarkColumnHeader: React.FC = () => (
  <div className={pagesStandardStyles.benchmarkHeader}>
    <span>80%</span>
    <span className={pagesStandardStyles.benchmarkHeaderRow}>
      {lang.pageContent.benchmark}
      <Tooltip
        title={lang.pageContent.benchmarkPerStoreAveragesNote}
        arrow
        enterTouchDelay={0}
        leaveTouchDelay={6000}>
        <InfoOutlinedIcon
          fontSize="small"
          className={pagesStandardStyles.benchmarkInfoIconHeader}
        />
      </Tooltip>
    </span>
  </div>
);

// Lookup map replaces the if/else chain that mapped columnName -> dataKey.
// 'sales' / 'orders' / 'units' map to themselves; anything else (i.e.
// 'grossProfit' or any unknown value) falls back to 'grossProfit', preserving
// the original `else` branch behavior.
const DATA_KEY_BY_COLUMN_NAME: Record<string, 'sales' | 'orders' | 'units' | 'grossProfit'> = {
  sales: 'sales',
  orders: 'orders',
  units: 'units',
};

// Options for buildPeerGroupColumns. Every value that differs between the
// first ('') and second ('2') peer-group blocks is an explicit, named property
// — no defaults — so a swap at the call site cannot happen silently.
// `isPrintView` and `printMinWidth` let the helper resolve the print-vs-data
// width internally, keeping createColumnGroup's body simpler.
interface PeerGroupColumnsOptions {
  columnName: string;
  suffix: '' | '2';
  isMultiStoreEnabled: boolean;
  isPrintView: boolean;
  printMinWidth: number;
  rankMinWidth: number;       // applied when isPrintView is false
  benchmarkMinWidth: number;  // applied when isPrintView is false
  rankValueGetter: GridColDef['valueGetter'];
  benchmarkValueGetter: GridColDef['valueGetter'];
  benchmarkRenderCell: GridColDef['renderCell'];
  columnConfig: {
    flex: number;
    headerClassName: string;
    align: 'right';
  };
}

// Builds the two peer-group columns (Peer Group Rank + 80% Benchmark) for a
// column group. Extracted to keep createColumnGroup under SonarQube's
// cognitive complexity threshold. The body is identical to what was inlined
// before — only the call surface changed.
const buildPeerGroupColumns = (opts: PeerGroupColumnsOptions): GridColDef[] => [
  {
    field: `${opts.columnName}PeerGroupRank${opts.suffix}`,
    // Change header text to MS based on multi-store status
    headerName: opts.isMultiStoreEnabled
      ? lang.pageContent.msGroupRank
      : lang.pageContent.peerGroupRank,
    ...opts.columnConfig,
    minWidth: opts.isPrintView ? opts.printMinWidth : opts.rankMinWidth,
    valueGetter: opts.rankValueGetter,
    renderCell: renderPeerGroupRankCell,
  },
  {
    field: `${opts.columnName}Benchmark${opts.suffix}`,
    headerName: lang.pageContent.percentBenchmark,
    ...opts.columnConfig,
    minWidth: opts.isPrintView ? opts.printMinWidth : opts.benchmarkMinWidth,
    valueGetter: opts.benchmarkValueGetter,
    renderCell: opts.benchmarkRenderCell,
    // Custom header to include benchmark explanation tooltip for multi-store mode
    ...(opts.isMultiStoreEnabled && {
      renderHeader: () => <BenchmarkColumnHeader />,
    }),
  },
];

// Module-level helpers that compute the largest absolute value across rows for
// a given numeric field. Extracted from createColumnGroup so they don't count
// toward the parent function's cognitive complexity. Behavior is identical to
// the previous inline closures.
type DataKey = 'sales' | 'orders' | 'units' | 'grossProfit';

const computeMaxAbs = (
  rows: GridRow[],
  salesData: SalesPerformanceDetailsByFulfillment,
  dataKey: DataKey,
  field: 'thisYear' | 'lastYear',
): number =>
  Math.max(
    0,
    ...rows.map(row =>
      Math.abs(salesData?.[dataKey]?.[row.category]?.[row.subcategory]?.[field] ?? 0),
    ),
  );

const computeMaxAbsChangeValue = (
  rows: GridRow[],
  salesData: SalesPerformanceDetailsByFulfillment,
  dataKey: DataKey,
): number =>
  Math.max(
    0,
    ...rows.map(row =>
      Math.abs(
        Number.parseFloat(
          salesData?.[dataKey]?.[row.category]?.[row.subcategory]?.changeValue?.value ?? '0',
        ) || 0,
      ),
    ),
  );

const computeMaxAbsBenchmark = (
  rows: GridRow[],
  salesData: SalesPerformanceDetailsByFulfillment,
  dataKey: DataKey,
): number =>
  Math.max(
    0,
    ...rows.map(row =>
      Math.abs(salesData?.[dataKey]?.[row.category]?.[row.subcategory]?.benchmark ?? 0),
    ),
  );

const SalesByFulfillment: React.FC<SalesByFulfillmentProps> = ({
  data: dataProp,
  isPrintView = false,
  visibleGroup,
}) => {
  const [showPeerGroupColumns, setShowPeerGroupColumns] = useState<boolean>(true);
  const reduxData = useAppSelector(selectSalesByFulfillment);
  const reduxLoading = useAppSelector(selectEcommLoading);
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);
  const handleAndLogClick = useClickHandler(PAGE_NM, TAB_NM);
  const buildDynamicUrl = useBuildDynamicUrl();
  const salesData = dataProp ?? reduxData;
  const isLoading = isPrintView ? false : reduxLoading;

  const handleCustomizeReportClick = useCallback((): void => {
    const url = buildDynamicUrl(BASE_URL_TYPE, 'SBF_CUSTOM_REPORT');
    handleAndLogClick(`${SECTION_NM} - Customize Report`, {
      url,
      urlTarget: '_blank',
    });
  }, [buildDynamicUrl, handleAndLogClick]);

  // Create fixed column rows with dataKey - DataGrid will use valueGetter to extract nested values
  // To add new categories: Add 4 new objects (1 totalForCategory + 3 subcategories) following the pattern below
  const rowData = useMemo(
    () => [
      // Demand category
      {
        id: 1,
        fixedColumn: lang.salesPerformanceFulFillmentContent.demand,
        dataKey: 'demand',
        category: 'demand' as const,
        subcategory: 'totalForCategory' as const,
      },
      {
        id: 2,
        fixedColumn: lang.salesPerformanceFulFillmentContent.bopis,
        dataKey: 'bopis',
        category: 'demand' as const,
        subcategory: 'bopis' as const,
      },
      {
        id: 3,
        fixedColumn: lang.salesPerformanceFulFillmentContent.bodfs,
        dataKey: 'bodfs',
        category: 'demand' as const,
        subcategory: 'bodfs' as const,
      },
      {
        id: 4,
        fixedColumn: lang.salesPerformanceFulFillmentContent.sth,
        dataKey: 'sth',
        category: 'demand' as const,
        subcategory: 'shipToHome' as const,
      },
      // Fulfilled category
      {
        id: 5,
        fixedColumn: lang.salesPerformanceFulFillmentContent.fulfilled,
        dataKey: 'fulfilled',
        category: 'fulfilled' as const,
        subcategory: 'totalForCategory' as const,
      },
      {
        id: 6,
        fixedColumn: lang.salesPerformanceFulFillmentContent.bopis,
        dataKey: 'bopis',
        category: 'fulfilled' as const,
        subcategory: 'bopis' as const,
      },
      {
        id: 7,
        fixedColumn: lang.salesPerformanceFulFillmentContent.bodfs,
        dataKey: 'bodfs',
        category: 'fulfilled' as const,
        subcategory: 'bodfs' as const,
      },
      {
        id: 8,
        fixedColumn: lang.salesPerformanceFulFillmentContent.sth,
        dataKey: 'sth',
        category: 'fulfilled' as const,
        subcategory: 'shipToHome' as const,
      },
      // Cancelled category
      {
        id: 9,
        fixedColumn: lang.salesPerformanceFulFillmentContent.cancelled,
        dataKey: 'cancelled',
        category: 'canceled' as const,
        subcategory: 'totalForCategory' as const,
      },
      {
        id: 10,
        fixedColumn: lang.salesPerformanceFulFillmentContent.bopis,
        dataKey: 'bopis',
        category: 'canceled' as const,
        subcategory: 'bopis' as const,
      },
      {
        id: 11,
        fixedColumn: lang.salesPerformanceFulFillmentContent.bodfs,
        dataKey: 'bodfs',
        category: 'canceled' as const,
        subcategory: 'bodfs' as const,
      },
      {
        id: 12,
        fixedColumn: lang.salesPerformanceFulFillmentContent.sth,
        dataKey: 'sth',
        category: 'canceled' as const,
        subcategory: 'shipToHome' as const,
      },
    ],
    [],
  );

  // Helper function to create column group with valueGetter
  // Wrapped in useCallback to maintain stable reference for useMemo dependencies
  const createColumnGroup = useCallback(
    (columnName: string, salesData: SalesPerformanceDetailsByFulfillment): GridColDef[] => {
      // Map columnName to data property (defaults to 'grossProfit' for any unrecognized name)
      const dataKey = DATA_KEY_BY_COLUMN_NAME[columnName] ?? 'grossProfit';

      // Sales and Gross Profit columns display currency ($); Orders and Units do not
      const isCurrencyGroup = dataKey === 'sales' || dataKey === 'grossProfit';
      const currencyPrefix = isCurrencyGroup ? '$' : '';

      // When printing, shrink columns to fit on paper; in normal view, size to data
      const printMinWidthWhenVisible = showPeerGroupColumns ? 90 : 120;
      const printMinWidth = visibleGroup ? printMinWidthWhenVisible : 58;

      // All columns use flex:1 so remaining space is shared; minWidth prevents truncation
      const columnConfig = {
        flex: 1,
        headerClassName: pagesStandardStyles.columnHeader,
        align: 'right' as const,
      };

      return [
        {
          field: `${columnName}ThisYear`,
          headerName: lang.salesPerformanceFulFillmentContent.thisYear,
          ...columnConfig,
          minWidth: isPrintView
            ? printMinWidth
            : minWidthForNumber(computeMaxAbs(rowData, salesData, dataKey, 'thisYear'), currencyPrefix),
          valueGetter: (_, row) => salesData[dataKey][row.category][row.subcategory].thisYear,
          renderCell: renderFormatCell,
        },
        ...(showPeerGroupColumns
          ? buildPeerGroupColumns({
              columnName,
              suffix: '',
              isMultiStoreEnabled,
              isPrintView,
              printMinWidth,
              rankMinWidth: 115.2,
              benchmarkMinWidth: minWidthForNumber(
                computeMaxAbsBenchmark(rowData, salesData, dataKey),
                currencyPrefix,
              ),
              rankValueGetter: (_, row) =>
                salesData[dataKey][row.category][row.subcategory].peerGroupRank,
              benchmarkValueGetter: (_, row) =>
                salesData[dataKey][row.category][row.subcategory].benchmark,
              benchmarkRenderCell: renderBenchmarkPercentCell,
              columnConfig,
            })
          : []),
        {
          field: `${columnName}LastYear`,
          headerName: lang.salesPerformanceFulFillmentContent.lastYear,
          ...columnConfig,
          minWidth: isPrintView
            ? printMinWidth
            : minWidthForNumber(computeMaxAbs(rowData, salesData, dataKey, 'lastYear'), currencyPrefix),
          valueGetter: (_, row) => salesData[dataKey][row.category][row.subcategory].lastYear,
          renderCell: renderFormatCell,
        },
        {
          field: `${columnName}ChangeValue`,
          headerName: lang.pageContent.changeOverLastYear,
          ...columnConfig,
          minWidth: isPrintView
            ? printMinWidth
            : minWidthForNumber(computeMaxAbsChangeValue(rowData, salesData, dataKey), `-${currencyPrefix}`),
          valueGetter: (_, row) => salesData[dataKey][row.category][row.subcategory].changeValue,
          renderCell: renderChangeCell,
        },
        {
          field: `${columnName}ChangePercent`,
          headerName: lang.pageContent.changeOverLastPercent,
          ...columnConfig,
          minWidth: isPrintView ? printMinWidth : 115.2,
          valueGetter: (_, row) => salesData[dataKey][row.category][row.subcategory].changePercent,
          renderCell: renderChangeNonZeroCell,
        },
        ...(showPeerGroupColumns
          ? buildPeerGroupColumns({
              columnName,
              suffix: '2',
              isMultiStoreEnabled,
              isPrintView,
              printMinWidth,
              rankMinWidth: 115.2,
              benchmarkMinWidth: 115.2,
              rankValueGetter: (_, row) =>
                salesData[dataKey][row.category][row.subcategory].changeOverLastYearPeerGroupRank,
              benchmarkValueGetter: (_, row) =>
                salesData[dataKey][row.category][row.subcategory].changeOverLastYearBenchmark,
              benchmarkRenderCell: renderPercentCell,
              columnConfig,
            })
          : []),
      ];
    },
    [showPeerGroupColumns, rowData, isPrintView, visibleGroup, isMultiStoreEnabled],
  );

  const salesColumns = useMemo(
    () => createColumnGroup('sales', salesData),
    [createColumnGroup, salesData],
  );
  const ordersColumns = useMemo(
    () => createColumnGroup('orders', salesData),
    [createColumnGroup, salesData],
  );
  const unitsColumns = useMemo(
    () => createColumnGroup('units', salesData),
    [createColumnGroup, salesData],
  );
  const grossProfitColumns = useMemo(
    () => createColumnGroup('grossProfit', salesData),
    [createColumnGroup, salesData],
  );

  // Get last column field names for groups
  // Used in DataGrid sx prop for styling borders
  const lastGroupOneField = useMemo(
    () => salesColumns[salesColumns.length - 1].field,
    [salesColumns],
  );
  const lastGroupTwoField = useMemo(
    () => ordersColumns[ordersColumns.length - 1].field,
    [ordersColumns],
  );
  const lastGroupThreeField = useMemo(
    () => unitsColumns[unitsColumns.length - 1].field,
    [unitsColumns],
  );

  // Map group name → column set for filtering
  const groupColumnsMap: Record<string, GridColDef[]> = useMemo(
    () => ({
      sales: salesColumns,
      orders: ordersColumns,
      units: unitsColumns,
      grossProfit: grossProfitColumns,
    }),
    [salesColumns, ordersColumns, unitsColumns, grossProfitColumns],
  );

  const columns: GridColDef[] = useMemo(() => {
    const fixedCol: GridColDef = {
      field: 'fixedColumn',
      headerName: '', // Empty header name for blank white space
      width: FIXED_COLUMN_WIDTH,
      headerClassName: pagesStandardStyles.columnHeader,
      cellClassName: pagesStandardStyles.stickyColumn,
    };

    // When visibleGroup is set, show only that group's columns
    if (visibleGroup) {
      return [fixedCol, ...(groupColumnsMap[visibleGroup] ?? [])];
    }

    return [fixedCol, ...salesColumns, ...ordersColumns, ...unitsColumns, ...grossProfitColumns];
  }, [
    salesColumns,
    ordersColumns,
    unitsColumns,
    grossProfitColumns,
    visibleGroup,
    groupColumnsMap,
  ]);

  // Group header style keyed by groupId for single-group mode
  const GROUP_STYLE_MAP: Record<string, { bg: string; color: string; border: string }> = {
    'sales-group': { bg: '#feeded', color: '#000', border: '2px solid #757575' },
    'orders-group': { bg: '#e5f7fd', color: '#000', border: '2px solid #757575' },
    'units-group': { bg: '#c7e5c8', color: '#000', border: '2px solid #757575' },
    'grossProfit-group': { bg: '#000000', color: '#ffffff', border: '1px solid #e0e0e0' },
  };

  // All possible group configs
  const allGroupConfigs = useMemo(
    () => [
      {
        key: 'sales' as const,
        groupId: 'sales-group',
        headerName: lang.pageContent.sales,
        columns: salesColumns,
      },
      {
        key: 'orders' as const,
        groupId: 'orders-group',
        headerName: lang.salesPerformanceFulFillmentContent.orders,
        columns: ordersColumns,
      },
      {
        key: 'units' as const,
        groupId: 'units-group',
        headerName: lang.salesPerformanceFulFillmentContent.units,
        columns: unitsColumns,
      },
      {
        key: 'grossProfit' as const,
        groupId: 'grossProfit-group',
        headerName: lang.pageContent.grossProfit.replace(' *', ''),
        columns: grossProfitColumns,
      },
    ],
    [salesColumns, ordersColumns, unitsColumns, grossProfitColumns],
  );

  // Define column grouping model for MUI DataGrid
  const columnGroupingModel: GridColumnGroupingModel = useMemo(() => {
    const groups = visibleGroup
      ? allGroupConfigs.filter(g => g.key === visibleGroup)
      : allGroupConfigs;

    return groups.map(g => ({
      groupId: g.groupId,
      headerName: g.headerName,
      children: g.columns.map(col => ({ field: col.field })),
    }));
  }, [allGroupConfigs, visibleGroup]);

  // Show skeleton while loading
  if (isLoading) {
    return (
      <SkeletonEcommSalesPerfGrid
        categoryCount={3}
        subCategoryCount={3}
      />
    );
  }

  // Show error if API failed to return data
  const errorMessage = lang.pageContent.dataUnavailableError.replace(
    '{section}',
    lang.ecommerceContent.salesByFulfillmentLabel,
  );
  if (salesData === null) {
    if (isPrintView) return null;
    return (
      <div className={styles.sectionContent}>
        <div className={pagesStandardStyles.pageHeader}>
          <div
            className={`${pagesStandardStyles.headerContainer} ${salesByFulfillmentStyles.headerContainer}`}>
            <div>
              <h2 className={pagesStandardStyles.pageTitle}>
                {lang.salesPerformanceFulFillmentContent.title}{' '}
                <span className={pagesStandardStyles.pageSubtitle}>
                  {lang.salesPerformanceFulFillmentContent.subTitle}
                </span>
              </h2>
              <p className={pagesStandardStyles.pageSubtitle}>{lang.pageContent.datePrefix} N/A</p>
            </div>
          </div>
        </div>
        <div className={pagesStandardStyles.errorContent}>
          <p>{errorMessage}</p>
        </div>
      </div>
    );
  } else if (Object.keys(salesData).length === 0) {
    if (isPrintView) return null;
    return (
      <div className={pagesStandardStyles.errorContent}>
        <p>{lang.pageContent.noData}</p>
      </div>
    );
  }

  return (
    <div className={styles.sectionContent}>
      {/* Page Header Title Section — hidden when rendering a single group slice (2nd/3rd/4th instance) */}
      {!visibleGroup || visibleGroup === 'sales' ? (
        <div className={pagesStandardStyles.pageHeader}>
          <div
            className={`${pagesStandardStyles.headerContainer} ${salesByFulfillmentStyles.headerContainer}`}>
            <div>
              <h2 className={pagesStandardStyles.pageTitle}>
                {lang.salesPerformanceFulFillmentContent.title}{' '}
                <span className={pagesStandardStyles.pageSubtitle}>
                  {lang.salesPerformanceFulFillmentContent.subTitle}
                </span>
              </h2>
              <p className={pagesStandardStyles.pageSubtitle}>
                {lang.pageContent.datePrefix}{' '}
                {isValidApiDate(salesData?.asOfDate) ? formatLoadDate(salesData.asOfDate) : 'N/A'}
              </p>
            </div>
            {!isPrintView && (
              <div
                className={`${pagesStandardStyles.headerActions} ${salesByFulfillmentStyles.headerActions}`}>
                {!isMobileDevice() && (
                  <MuiButton
                    colors="secondary"
                    iconPosition="right"
                    iconType="launch"
                    onClick={handleCustomizeReportClick}>
                    {lang.pageContent.customizeReport}
                  </MuiButton>
                )}
                <div className={pagesStandardStyles.switchWrapper}>
                  <MuiSwitch
                    key={showPeerGroupColumns.toString()}
                    color="primary"
                    defaultChecked={showPeerGroupColumns}
                    label={lang.pageContent.toggleShowPeerGroup}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setShowPeerGroupColumns(event.target.checked);
                    }}
                    size="medium"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* DataGrid with Column Grouping */}
      <div>
        <DataGrid
          // === REQUIRED DATA PROPS ===
          rows={rowData}
          columns={columns}
          columnGroupingModel={columnGroupingModel}
          // === STANDARD BEHAVIOR (Recommended for all grids) ===
          disableRowSelectionOnClick
          disableVirtualization
          disableColumnMenu
          disableColumnSorting
          disableColumnResize
          hideFooter
          autoHeight
          // === STYLING & LAYOUT ===
          className={pagesStandardStyles.dataGrid}
          getRowClassName={params => {
            // Apply gray background (#f5f5f5) to category rows (Demand, Fulfilled, Cancelled)
            // Apply white background (#ffffff) to subcategory rows (BOPIS, BODFS, STH)
            return params.row.subcategory === 'totalForCategory'
              ? pagesStandardStyles.oddRow
              : pagesStandardStyles.evenRow;
          }}
          // === CUSTOMIZABLE SETTINGS (Adjust per grid if needed) ===
          columnHeaderHeight={52}
          columnBufferPx={1500}
          // === CUSTOM STYLES (Grid-specific overrides) ===
          sx={{
            // Add border-bottom to column headers container
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: '1px solid #c9c9c9',
            },
            // When only one group is visible, it always lands at nth-of-type(3).
            // Apply the correct color for the active group.
            ...(visibleGroup
              ? {
                  '& .MuiDataGrid-columnHeader--filledGroup:nth-of-type(3)': {
                    backgroundColor: GROUP_STYLE_MAP[`${visibleGroup}-group`]?.bg,
                    color: GROUP_STYLE_MAP[`${visibleGroup}-group`]?.color,
                    borderRight: GROUP_STYLE_MAP[`${visibleGroup}-group`]?.border,
                    borderBottom: '1px solid #c9c9c9',
                  },
                }
              : {
                  '& .MuiDataGrid-columnHeader--filledGroup:nth-of-type(3)': {
                    backgroundColor: '#feeded', // Sales group - light pink
                    color: '#000',
                    borderRight: '2px solid #757575',
                    borderBottom: '1px solid #c9c9c9',
                  },
                  '& .MuiDataGrid-columnHeader--filledGroup:nth-of-type(4)': {
                    backgroundColor: '#e5f7fd', // Orders group - light blue
                    color: '#000',
                    borderRight: '2px solid #757575',
                    borderBottom: '1px solid #c9c9c9',
                  },
                  '& .MuiDataGrid-columnHeader--filledGroup:nth-of-type(5)': {
                    backgroundColor: '#c7e5c8', // Units group - light green
                    color: '#000',
                    borderRight: '2px solid #757575',
                    borderBottom: '1px solid #c9c9c9',
                  },
                  '& .MuiDataGrid-columnHeader--filledGroup:nth-of-type(6)': {
                    backgroundColor: '#000000', // Gross Profit $ group - black
                    color: '#ffffff',
                    borderRight: '1px solid #e0e0e0',
                    borderBottom: '1px solid #c9c9c9',
                  },
                }),
            ...getGroupColumnHeaderStyles(
              lastGroupOneField,
              lastGroupTwoField,
              lastGroupThreeField,
            ),
            ...getDataGridStyles('fixedColumn'),
            // Remove bottom border from all regular column headers (must come AFTER getDataGridStyles)
            '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--filledGroup)': {
              borderBottom: 'none !important', // !important needed to override getDataGridStyles and Pages.module.css
            },
            // Override fixed column header: white background, sticky position, standard border
            '& .MuiDataGrid-columnHeader[data-field="fixedColumn"]': {
              backgroundColor: '#ffffff',
              position: 'sticky',
              left: 0,
              zIndex: 3,
              borderRight: '1px solid #c9c9c9',
            },
            // Subcategory rows (BOPIS, BODFS, STH): lighter font weight
            '& .MuiDataGrid-row:not(:nth-of-type(1)):not(:nth-of-type(5)):not(:nth-of-type(9)) .MuiDataGrid-cell[data-field="fixedColumn"]':
              {
                fontWeight: 400,
              },
          }}
        />
      </div>

      {/* Footer Disclaimer — only show on the last group or when showing all */}
      {(!visibleGroup || visibleGroup === 'grossProfit') && (
        <div className={pagesStandardStyles.disclaimer}>
          <div>{lang.ecommerceContent.salesByFulfillmentDisclaimerLine1}</div>
          <div>{lang.ecommerceContent.salesByFulfillmentDisclaimerLine2}</div>
          <div>{lang.ecommerceContent.salesByFulfillmentDisclaimerLine3}</div>
        </div>
      )}

      {/* Print-only benchmark note below the Sales grid (multi-store only) */}
      {isPrintView && isMultiStoreEnabled && visibleGroup === 'sales' && (
        <div className={pagesStandardStyles.printBenchmarkNote}>
          <InfoOutlinedIcon
            fontSize="small"
            className={pagesStandardStyles.printBenchmarkNoteIcon}
          />
          <span>{lang.pageContent.benchmarkPerStoreAveragesNote}</span>
        </div>
      )}

    </div>
  );
};

export default SalesByFulfillment;
